// /assistant/enrol?t=… — opened on the assistant's PHONE from the QR code.
// Public: works with nobody signed in, so it makes no authenticated calls.
import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser';
import { enrolOptions, enrolPhone, AssistantError, totpOptions, totpEnrol, type TotpSetup } from '../lib/assistantApi';

export function enrolTokenFromSearch(search: string): string {
  return new URLSearchParams(search).get('t') ?? '';
}

type Stage =
  | { s: 'loading' }
  | { s: 'ready'; name: string; options: PublicKeyCredentialCreationOptionsJSON }
  | { s: 'working'; name: string; options: PublicKeyCredentialCreationOptionsJSON }
  | { s: 'done'; name: string }
  | { s: 'error'; message: string };

type TotpStage =
  | { s: 'idle' }
  | { s: 'working' }
  | { s: 'confirm'; setup: TotpSetup }
  | { s: 'done'; username: string }
  | { s: 'error'; message: string };

/** Google Authenticator set-up: link button, text secret, then one code to confirm. */
export function TotpSetupBox({ token }: { token: string }): JSX.Element {
  const [st, setSt] = useState<TotpStage>({ s: 'idle' });
  const [code, setCode] = useState('');
  const [note, setNote] = useState<string | null>(null);

  async function start(): Promise<void> {
    setNote(null);
    setSt({ s: 'working' });
    try { setSt({ s: 'confirm', setup: await totpOptions(token) }); } catch (err) {
      setSt({ s: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }
  async function confirm(setup: TotpSetup): Promise<void> {
    setNote(null);
    try {
      const r = await totpEnrol(token, setup.sealed, code.trim());
      setSt({ s: 'done', username: r.username });
    } catch (err) {
      setNote(err instanceof Error ? err.message : String(err));
    }
  }
  return (
    <div data-testid="totp-setup" style="margin-top:24px;border-top:1px solid #ccc;padding-top:16px">
      {st.s === 'idle' || st.s === 'working' ? (
        <button type="button" data-testid="totp-start" disabled={st.s === 'working'} style="width:100%;padding:14px;font-size:18px" onClick={() => void start()}>
          Set up Google Authenticator
        </button>
      ) : null}
      {st.s === 'error' ? <p role="alert" data-testid="totp-error">{st.message}</p> : null}
      {st.s === 'confirm' ? (
        <>
          <p>1. Tap the button to add nvManager to your Authenticator app.</p>
          <p><a href={st.setup.otpauth} data-testid="totp-link" style="display:block;padding:14px;border:1px solid #333">Open in Authenticator</a></p>
          <p>Or type this key into the app by hand:</p>
          <p><code data-testid="totp-secret" style="word-break:break-all">{st.setup.secret}</code></p>
          <p>2. Type the 6-digit code the app shows.</p>
          <input
            type="text" inputMode="numeric" data-testid="totp-code" aria-label="6-digit code" maxLength={7}
            autocomplete="one-time-code" value={code}
            onInput={(e: Event) => setCode((e.target as HTMLInputElement).value)}
            style="width:100%;padding:12px;font-size:20px;text-align:center"
          />
          <button type="button" data-testid="totp-confirm" style="width:100%;padding:14px;font-size:18px;margin-top:8px" onClick={() => void confirm(st.setup)}>
            Confirm
          </button>
          {note ? <p role="alert" data-testid="totp-note">{note}</p> : null}
        </>
      ) : null}
      {st.s === 'done' ? (
        <p data-testid="totp-done">
          Authenticator set up. At the front desk choose Sign in with a code and use the username <strong>{st.username}</strong>.
        </p>
      ) : null}
    </div>
  );
}

export function AssistantEnrolPage(): JSX.Element {
  const token = enrolTokenFromSearch(window.location.search);
  const [stage, setStage] = useState<Stage>(token ? { s: 'loading' } : {
    s: 'error', message: 'This link is incomplete. Ask your manager for a new setup code.',
  });
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let live = true;
    enrolOptions(token).then(
      (r) => { if (live) setStage({ s: 'ready', name: r.display_name, options: r.options }); },
      (err) => { if (live) setStage({ s: 'error', message: err instanceof Error ? err.message : String(err) }); },
    );
    return () => { live = false; };
  }, [token]);

  async function setUp(name: string, options: PublicKeyCredentialCreationOptionsJSON): Promise<void> {
    setNote(null);
    setStage({ s: 'working', name, options });
    try {
      await enrolPhone(token, options);
      setStage({ s: 'done', name });
    } catch (err) {
      setNote(err instanceof Error ? err.message : String(err));
      // A cancelled prompt can be retried with the same challenge; a server
      // refusal (expired / already used) cannot.
      if (err instanceof AssistantError && err.kind === 'cancelled') setStage({ s: 'ready', name, options });
      else if (err instanceof AssistantError && err.kind === 'other' && !/phone could not/i.test(err.message)) {
        setStage({ s: 'error', message: err.message });
      } else setStage({ s: 'ready', name, options });
    }
  }

  return (
    <div class="assistant-enrol" data-testid="assistant-enrol"
      style="max-width:420px;margin:0 auto;padding:24px 16px;text-align:center;font-size:18px">
      <h1 style="font-size:22px">nvManager</h1>
      {stage.s === 'loading' ? <p>Checking your setup code...</p> : null}
      {stage.s === 'error' ? <p role="alert" data-testid="enrol-error">{stage.message}</p> : null}
      {stage.s === 'ready' || stage.s === 'working' ? (
        <>
          <p>Hello, <strong>{stage.name}</strong>. Set up this phone so you can sign in to nvManager at the front desk.</p>
          <button
            type="button"
            data-testid="enrol-button"
            disabled={stage.s === 'working'}
            style="width:100%;padding:18px;font-size:20px"
            onClick={() => void setUp(stage.name, stage.options)}
          >
            {stage.s === 'working' ? 'Setting up...' : 'Set up on this phone'}
          </button>
          {note ? <p role="alert" data-testid="enrol-note">{note}</p> : null}
        </>
      ) : null}
      {stage.s === 'done' ? (
        <p data-testid="enrol-done">
          Done — you can now sign in at the front desk by choosing Sign in with your phone (Face ID).
        </p>
      ) : null}
      {token && (stage.s === 'ready' || stage.s === 'working' || stage.s === 'done') ? <TotpSetupBox token={token} /> : null}
    </div>
  );
}
